import React, { FC } from 'react';
import styles from './LandscapeProjectsAddRequestedSupportOrganism.module.css';
import cn from 'classnames';
import { Button, Checkbox, RadioButton } from '../../atoms';
import { Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getUUID } from '../../../utils/UUID';
import { ProjectInKindSupport } from '../../../store/landscapeAPI/project/project-in-kind-support';
import { ProjectSupportType } from '../../../store/landscapeAPI/project/project-support-type';
import { ProjectSupportResponseDto } from '../../../store/landscapeAPI/project/dto/response/project-support-response.dto';
import { ProjectSupportState } from '../../pages/LandscapeProjectsAddProjectsRequestedSupportPage/LandscapeProjectsAddProjectsRequestedSupportPage';

interface LandscapeProjectsAddRequestedSupportOrganismProps {
  projectSupportState: ProjectSupportState;
  setProjectSupportState: React.Dispatch<React.SetStateAction<any>>;
  handleSubmit: () => void;
}

const LandscapeProjectsAddRequestedSupportOrganism: FC<
  LandscapeProjectsAddRequestedSupportOrganismProps
> = ({ projectSupportState, setProjectSupportState, handleSubmit }) => {
  const { projectId } = useParams();

  const createSupport = (
    supportType: ProjectInKindSupport
  ): ProjectSupportResponseDto => ({
    contributionGoal: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    description: '',
    id: getUUID(),
    inKindSupport: supportType,
    language: '',
    minimumFinancialContribution: 0,
    projectId: projectId!,
    published: false,
    raised: 0,
    supportType: ProjectSupportType.InKind,
    versionId: 0,
  });

  const updateState = (key: string, value: any) => {
    setProjectSupportState((prevState: any) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSupportChecked = (
    checked: boolean,
    supportType: ProjectInKindSupport,
    key: string
  ) => {
    updateState(key, checked);

    const supportExists = projectSupportState.projectInKindSupport.some(
      (support) => support.inKindSupport === supportType
    );

    if (supportExists && checked) return;

    if (!supportExists && checked) {
      updateState('projectInKindSupport', [
        ...projectSupportState.projectInKindSupport,
        createSupport(supportType),
      ]);
    }

    if (supportExists && !checked) {
      updateState(
        'projectInKindSupport',
        projectSupportState.projectInKindSupport.filter(
          (support) => support.inKindSupport !== supportType
        )
      );
    }
  };

  const inKindSupportItems = [
    {
      label: 'Training And Extension',
      isChecked: projectSupportState.isTrainingChecked,
      key: 'isTrainingChecked',
      supportType: ProjectInKindSupport.TrainingAndExtension,
    },
    {
      label: 'Technology Transfer',
      isChecked: projectSupportState.isTechnologyChecked,
      key: 'isTechnologyChecked',
      supportType: ProjectInKindSupport.TechnologyTransfer,
    },
    {
      label: 'Alternative Livelihoods Activities',
      isChecked: projectSupportState.isAlternativeChecked,
      key: 'isAlternativeChecked',
      supportType: ProjectInKindSupport.AlternativeLivelihoodsActivities,
    },
    {
      label: 'Landscape Restoration',
      isChecked: projectSupportState.isLandscapeChecked,
      key: 'isLandscapeChecked',
      supportType: ProjectInKindSupport.LandscapeRestoration,
    },
    {
      label: 'Enhancing Access To Finance',
      isChecked: projectSupportState.isEnhancingChecked,
      key: 'isEnhancingChecked',
      supportType: ProjectInKindSupport.EnhancingAccessToFinance,
    },
    {
      label: 'Other',
      isChecked: projectSupportState.isOtherChecked,
      key: 'isOtherChecked',
      supportType: ProjectInKindSupport.Other,
    },
  ];

  return (
    <Form className={cn(styles.landscapeWrapper)}>
      <Form.Group className="mb-3">
        <Form.Label>Financial support type</Form.Label>
        <RadioButton
          defaultChecked={projectSupportState.projectContainsFinancialSupport}
          onChange={(checked) =>
            updateState('projectContainsFinancialSupport', checked)
          }
        ></RadioButton>
      </Form.Group>

      {projectSupportState.projectContainsFinancialSupport && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Required Funding</Form.Label>
            <Form.Control
              type="text"
              value={
                projectSupportState.projectFinancing?.contributionGoal ?? ''
              }
              onChange={(e) =>
                updateState('projectFinancing', {
                  ...projectSupportState.projectFinancing,
                  contributionGoal: Number(e.target.value),
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Minimum Financial Contribution</Form.Label>
            <Form.Control
              type="text"
              value={
                projectSupportState.projectFinancing
                  ?.minimumFinancialContribution ?? ''
              }
              onChange={(e) =>
                updateState('projectFinancing', {
                  ...projectSupportState.projectFinancing,
                  minimumFinancialContribution: Number(e.target.value),
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Raised Funds</Form.Label>
            <Form.Control
              type="text"
              value={projectSupportState.projectFinancing?.raised ?? ''}
              onChange={(e) =>
                updateState('projectFinancing', {
                  ...projectSupportState.projectFinancing,
                  raised: Number(e.target.value),
                })
              }
            />
          </Form.Group>
        </>
      )}

      <Form.Group className="mb-3">
        <Form.Label>In-kind support type</Form.Label>
        <RadioButton
          defaultChecked={projectSupportState.projectContainsInKindSupport}
          onChange={(checked) =>
            updateState('projectContainsInKindSupport', checked)
          }
        ></RadioButton>
      </Form.Group>

      {projectSupportState.projectContainsInKindSupport &&
        inKindSupportItems.map((item) => (
          <Form.Group className="mb-3" key={item.supportType}>
            <Checkbox
              checked={item.isChecked}
              onChange={(checked) =>
                handleSupportChecked(checked, item.supportType, item.key)
              }
              label={item.label}
              id={item.label}
            ></Checkbox>
          </Form.Group>
        ))}

      <Button onClick={handleSubmit} variant="dark">
        Save
      </Button>
    </Form>
  );
};

export default LandscapeProjectsAddRequestedSupportOrganism;
